import type { components } from "../lib/api/schema";

export interface ApiErrorCause {
  /**
   * The HTTP status code returned by the API, e.g. 400, 401, 403, 404, 500, etc.
   */
  status: number;

  /**
   * Whether the response is a job's own answer
   * (`X-Job-Completed`): the handler ran and this is its outcome.
   */
  jobCompleted?: boolean;

  /**
   * The message associated with the status code, either returned by the API or a generic message based on the status code.
   */
  statusMessage: string;

  /**
   * Seconds from the response's Retry-After header, when the API sent one (503 backpressure).
   */
  retryAfterSeconds?: number;

  /**
   * The envelope's `error.code` (`appretio.error.ErrorCode`), when the API sent one.
   */
  code?: components["schemas"]["ErrorCode"];

  /**
   * The envelope's `error.data`, when the API sent one.
   */
  data?: components["schemas"]["Error"]["data"];
}

/**
 * An error raised in the browser whose message tells the user what to change.
 * The toast shows the message as is, with `title` over the request's own.
 */
export class UserFacingError extends Error {
  constructor(
    message: string,
    readonly title?: string
  ) {
    super(message);
    this.name = "UserFacingError";
  }
}

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout"
};

/**
 * Helper function for retrieving the important parts of the API error and query information
 */
export function parseApiError(
  error: Error,
  errorMessage?: string
): {
  title: string;
  description: string;
} {
  const cause = error.cause as ApiErrorCause | undefined;

  if (error instanceof UserFacingError) {
    return {
      title: error.title ?? errorMessage ?? "Something went wrong.",
      description: error.message
    };
  }

  /**
   * If we are in development mode, or if verbose errors are enabled,
   * return the full error details for developer ease
   */
  if (
    import.meta.env.MODE === "development" ||
    import.meta.env.VITE_ENABLE_VERBOSE_ERRORS === "true"
  ) {
    let errorTitle = "";
    if (errorMessage) {
      errorTitle += `${errorMessage}`;
    }

    if (cause?.status) {
      errorTitle += ` (${cause.status} - ${HTTP_STATUS_MESSAGES[cause.status] ?? "Unknown Status"})`;
    }

    return {
      title: errorTitle ?? "Something went wrong.",
      description: cause?.statusMessage ?? "An unexpected error occurred."
    };
  }

  /**
   * A 4xx is the server rejecting the specific request with an actionable
   * reason (a guard, a validation rule) — show that reason, not a generic
   * line the user cannot act on. So is a 503 with Retry-After: the server
   * was busy, and a save the client does not retry should say to try again.
   * So is a 504: the query ran out of time, and nothing retries it.
   * Other 5xx / network errors stay generic.
   */
  const status = cause?.status ?? 0;
  const actionable = status >= 400 && status < 500;
  const busy = status === 503 && !!cause?.retryAfterSeconds;
  const timedOut = status === 504;
  if (cause?.statusMessage && (actionable || busy || timedOut)) {
    return {
      title: errorMessage ?? "Something went wrong.",
      description: cause.statusMessage
    };
  }

  /**
   * Return simplified version for non-development environment
   */
  return {
    title: "Something went wrong.",
    description: errorMessage ?? "An unexpected error occurred."
  };
}
